"use client"

import type React from "react"
import Image from "next/image"
import Link from "next/link"
import { HiArrowRight, HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { useState } from "react"
import { Plus_Jakarta_Sans } from "next/font/google"

import { useGetAllNewsQuery } from "@/hooks/useGetAllNewsQueryHook/useGetAllNewsQueryHook"
import { formatDateVN } from "@/utils/client/date"

const jakarta = Plus_Jakarta_Sans({
    subsets: ['vietnamese'],
    weight: ['400', '500', '600', '700'],
    display: 'swap'
})

const MEDIA_NEWS_NEWTYPE_ID = process.env.NEXT_PUBLIC_MEDIA_NEWS_NEWTYPE_ID

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

interface NewsCardProps {
    image: string | null
    category: string
    date: string
    title: string
    description: string | null
    className?: string
}

const NewsCard: React.FC<NewsCardProps> = ({
    image,
    category,
    date,
    title,
    description,
    className,
}) => {
    const isExternalImage = image && (image.startsWith('http://') || image.startsWith('https://'))

    return (
        <div className={cn("group flex flex-col gap-3 2xl:gap-4 min-[1920px]:gap-6 overflow-hidden h-full", className)}>
            <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-gray-50">
                {isExternalImage ? (
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                    />
                ) : (
                    <Image
                        src={image || "/placeholder.svg"}
                        alt={title}
                        fill
                        className="object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                    />
                )}


                <div className="absolute top-3 left-3 rounded-full bg-[#005a32] px-3 py-1 min-[1920px]:px-4 min-[1920px]:py-2 text-[10px] min-[1920px]:text-base font-medium text-white shadow-sm">
                    {category}
                </div>
            </div>

            <div className="flex flex-col gap-2 min-[1920px]:gap-4 flex-1">
                <div className="text-xs 2xl:text-sm min-[1920px]:text-lg text-[#9F9F9F] font-normal">
                    Ngày đăng • <span className="text-[#2D3746]">{date}</span>
                </div>

                <h3 className={cn(
                    jakarta.className,
                    "line-clamp-2 text-base 2xl:text-lg min-[1920px]:text-2xl font-bold leading-snug text-[#2D3746] transition-colors duration-300 group-hover:text-[#005a32]"
                )}>
                    {title}
                </h3>

                <p className={cn(
                    jakarta.className,
                    "line-clamp-2 text-xs 2xl:text-sm min-[1920px]:text-lg leading-relaxed text-[#2D3746]"
                )}>
                    {description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incidid..."}
                </p>
            </div>
        </div>
    )
}

const ITEMS_PER_PAGE = 3

export const NewsHome: React.FC = () => {
    const { data, isLoading, isError } = useGetAllNewsQuery()

    const [pageState, setPageState] = useState({ page: 0, direction: 'next' })
    const currentPage = pageState.page

    // --- 1. Hàm tạo Slug chuẩn ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const allNews = data?.result.items ?? []
    const totalPages = Math.ceil(allNews.length / ITEMS_PER_PAGE)

    const displayedNews = allNews.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    )

    const goToPrevPage = () => {
        setPageState(prev => ({
            page: Math.max(0, prev.page - 1),
            direction: 'prev'
        }))
    }

    const goToNextPage = () => {
        setPageState(prev => ({
            page: Math.min(totalPages - 1, prev.page + 1),
            direction: 'next'
        }))
    }

    const navButtonClasses = cn(
        "absolute top-1/2 -translate-y-1/2 z-10",

        "flex items-center justify-center size-9 2xl:size-12 min-[1920px]:size-16 rounded-full",
        "bg-transparent border border-gray-500",
        "text-gray-500 opacity-70 transition-all duration-300",
        "hover:border-black hover:text-black hover:scale-110 hover:opacity-100",
        "disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-500 disabled:hover:scale-100 disabled:hover:opacity-30"
    )

    return (

        <section className="py-12 px-4 xl:px-8 2xl:px-16 min-[1920px]:py-24 min-[1920px]:px-32 bg-white overflow-hidden">
            <style jsx>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-slide-next {
                    animation: slideInRight 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
                .animate-slide-prev {
                    animation: slideInLeft 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
            `}</style>

            <div className="mx-auto max-w-4xl 2xl:max-w-6xl min-[1920px]:max-w-[110rem] relative">

                <div className="mb-5 min-[1920px]:mb-12 mt-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                    <div className="max-w-xl 2xl:max-w-3xl min-[1920px]:max-w-5xl space-y-2 min-[1920px]:space-y-4">

                        <h2 className={cn(
                            jakarta.className,
                            "text-2xl font-semibold text-[#2D3746] xl:text-3xl 2xl:text-4xl min-[1920px]:text-6xl tracking-tight"
                        )}>
                            Truyền thông Rovi Holdings
                        </h2>


                        <p
                            className="text-sm 2xl:text-base min-[1920px]:text-xl leading-relaxed text-[#2D3746]"
                            style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                        >
                            Cập nhật nhanh những tin tức mới nhất, hoạt động nổi bật và thông báo quan trọng từ Rovi Holdings – Nơi kết nối thông tin và tầm nhìn chiến lược
                        </p>
                    </div>

                    <Link
                        href="/news?tin-truyen-thong"

                        className="group inline-flex items-center gap-2 bg-[#005a32] hover:bg-red-600 text-white px-5 py-2 2xl:px-7 2xl:py-3 min-[1920px]:px-10 min-[1920px]:py-5 rounded-full font-semibold text-xs 2xl:text-sm min-[1920px]:text-xl transition-colors duration-300 whitespace-nowrap"
                    >
                        Xem tất cả
                        <HiArrowRight className="size-3.5 2xl:size-4 min-[1920px]:size-6 text-white" />
                    </Link>
                </div>

                <div className="relative">

                    <button
                        onClick={goToPrevPage}
                        disabled={currentPage === 0 || isLoading || isError}
                        className={cn(navButtonClasses, "-left-12 xl:-left-30 2xl:-left-40 min-[1920px]:-left-60")}
                        aria-label="Previous page"
                    >
                        <HiChevronLeft className="size-5 2xl:size-6 min-[1920px]:size-8" />
                    </button>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages - 1 || isLoading || isError || totalPages === 0}
                        className={cn(navButtonClasses, "-right-12 xl:-right-30 2xl:-right-40 min-[1920px]:-right-60")}
                        aria-label="Next page"
                    >
                        <HiChevronRight className="size-5 2xl:size-6 min-[1920px]:size-8" />
                    </button>

                    <div className="min-h-[350px] min-[1920px]:min-h-[600px]">
                        {isLoading && (

                            <div className="grid grid-cols-1 gap-5 2xl:gap-10 min-[1920px]:gap-20 sm:grid-cols-2 xl:grid-cols-3">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="animate-pulse flex flex-col gap-3">
                                        <div className="aspect-[16/10] rounded-xl bg-gray-100" />
                                        <div className="space-y-2">
                                            <div className="h-3 w-28 rounded bg-gray-100" />
                                            <div className="h-5 w-full rounded bg-gray-100" />
                                            <div className="h-3 w-2/3 rounded bg-gray-100" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {isError && (
                            <div className="flex h-[250px] items-center justify-center">
                                <p className="text-gray-500 text-sm font-medium">Không thể tải tin tức. Vui lòng thử lại sau.</p>
                            </div>
                        )}

                        {!isLoading && !isError && allNews.length === 0 && (
                            <div className="flex h-[250px] items-center justify-center">
                                <p className="text-gray-500 text-sm font-medium">Chưa có tin tức nào.</p>
                            </div>
                        )}

                        {!isLoading && !isError && allNews.length > 0 && (
                            <div
                                key={currentPage}
                                className={cn(

                                    "grid grid-cols-1 gap-5 2xl:gap-10 min-[1920px]:gap-20 sm:grid-cols-2 xl:grid-cols-3",
                                    pageState.direction === 'next' ? 'animate-slide-next' : 'animate-slide-prev'
                                )}
                            >
                                {displayedNews.map((item) => {
                                    // --- 2. Tạo URL: ten-bai-viet-{id} ---
                                    const slugTitle = createSlug(item.title || '');
                                    const postHref = `/news/${slugTitle}-${item.id}`;

                                    return (
                                        <Link href={postHref} key={item.id} className="block h-full hover:no-underline">
                                            <NewsCard
                                                image={item.image}
                                                category={item.newsType?.newsTypeName || "Tin truyền thông"}
                                                date={formatDateVN(item.updatedDateTS)}
                                                title={item.title}
                                                description={item.description}
                                            />
                                        </Link>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default NewsHome